import { z } from 'zod';

// ---------------------------------------------------------------------------
// GET /v1/public-reviews?locale=&limit=&offset=
//
// Public text-review facade over `customer_reviews` (+ optional
// `customer_review_photos` / `wallpapers_i18n` fan-out), for the /opinie page
// (spec 2026-07-17-public-reviews-page, T2). Deliberately DISTINCT from the
// frozen `/v1/customer-reviews` gallery contract (customer-reviews.ts):
//
//   - gallery predicate:      published=true AND wallpaper_id IS NOT NULL
//   - public-reviews predicate: published=true AND created_at <= now()
//
// The text page shows ALL published reviews including standalone testimonials
// with wallpaper_id NULL — text is primary, a linked wallpaper/photo is
// optional enrichment, not a requirement (spec "Data / visibility").
//
// Additive to the frozen /v1/ contract — a NEW route, no changes to the
// existing /v1/customer-reviews gallery route or its schemas.
// ---------------------------------------------------------------------------

export const PublicReviewsRequestSchema = z.object({
  /** Optional BCP-47-ish locale filter (pl|en|de|cs). Omitted → all published<=now. */
  locale: z.string().min(2).max(5).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(24),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PublicReviewsRequest = z.infer<typeof PublicReviewsRequestSchema>;

export const PublicReviewItemSchema = z.object({
  id: z.number().int().positive(),
  authorName: z.string().nullable(),
  text: z.string().nullable(),
  rating: z.number().int().min(1).max(5).nullable(),
  /** RFC3339 'Z' ISO datetime — normalised through `new Date(...).toISOString()`. */
  createdAt: z.string().datetime(),
  locale: z.string(),
  /** First photo (sort_order ASC) for this review, if any were attached. */
  photoUrl: z.string().url().nullable(),
  /** Present only when wallpaper_id is set AND a locale-resolved i18n row exists. */
  wallpaperSlug: z.string().nullable(),
  wallpaperName: z.string().nullable(),
});
export type PublicReviewItem = z.infer<typeof PublicReviewItemSchema>;

/**
 * Aggregate rating over rated, published, created_at<=now() reviews.
 * Same `reviewCount === 0 <=> ratingValue === null` invariant as
 * ProductReviewsResponseSchema (reuses computeRatingValue).
 */
export const PublicReviewsAggregateSchema = z.object({
  ratingValue: z.number().min(1).max(5).nullable(),
  reviewCount: z.number().int().nonnegative(),
});
export type PublicReviewsAggregate = z.infer<typeof PublicReviewsAggregateSchema>;

export const PublicReviewsResponseSchema = z.object({
  items: z.array(PublicReviewItemSchema),
  /** Count of published AND created_at<=now() reviews (pagination + AggregateRating math). */
  total: z.number().int().nonnegative(),
  aggregate: PublicReviewsAggregateSchema,
});
export type PublicReviewsResponse = z.infer<typeof PublicReviewsResponseSchema>;
